import { useEffect, useState } from 'react';
import { useLoginCustomer, useRegisterCustomer } from 'hooks';
import { Icon, RichText, SiteLink } from 'components/shared';
import { getResource, getResourceImage, getResourceText } from 'lib/resources';
import { isString } from 'underscore';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { addNewLines } from 'components/sections/articles/richTextOptions';
import Button from 'components/shared/button';
import { signIn, useSession } from 'next-auth/react';
import getMultipassToken from 'hooks/customer/useMultipassToken';
import { getLinkHref } from 'lib/utils/helpers';
import { Spinner } from 'components/shared/icons/ui';
import AccountWrapper from './account-wrapper';
import { PREFIX as accountSlug } from '../dashboard/slugs';

const richTextOptions = richText => {
  const hyperlink = richText?.links?.entries?.hyperlink || [];
  const getLinkedEntry = linkId => hyperlink.find(l => l?.sys?.id === linkId);

  return {
    renderNode: {
      [BLOCKS.UL_LIST]: (_, children) => <ul className="list-disc">{children}</ul>,
      [BLOCKS.LIST_ITEM]: (_, children) => {
        const addBottomMargin = c =>
          c.type === 'p' ? { ...c, props: { ...c.props, className: `${c.props.className}` } } : c;
        const content = children.map(addBottomMargin);

        return (
          <li className="mb-2.5 flex list-none items-center gap-2.5">
            <Icon className="h-3 w-3" type="check" />
            {content}
          </li>
        );
      },
      [BLOCKS.PARAGRAPH]: (_, children) => {
        const isEmpty = children.length === 1 && children[0] === '';
        if (isEmpty) {
          return <br />;
        }

        return <p className="text-body-2">{addNewLines(children)}</p>;
      },
      [INLINES.HYPERLINK]: (node, children) => {
        const { uri } = node.data;
        return (
          <SiteLink external={uri} className="underline">
            {children}
          </SiteLink>
        );
      },
      [INLINES.ENTRY_HYPERLINK]: (node, children) => {
        const {
          data: {
            target: {
              sys: { id },
            },
          },
        } = node;

        const link = getLinkedEntry(id);

        if (!link) {
          return children;
        }

        return (
          <SiteLink href={getLinkHref(link)} className="underline">
            {children}
          </SiteLink>
        );
      },
    },
  };
};

const Register = ({
  page: {
    resources: { global, access },
  },
  localeData: { currency },
}) => {
  const tGlobal = getResourceText(global);
  const t = getResourceText(access);

  const [registerCustomer, { loading: registerLoading, error: registerError }] =
    useRegisterCustomer({ currency });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { data: session, status } = useSession();
  const [acceptsMarketing, setAcceptsMarketing] = useState();
  const [loginCustomer, { loading: loginLoading, error: errorLogIn }] = useLoginCustomer({
    currency,
  });
  const loading =
    (status === 'loading' || status === 'authenticated') ?? registerLoading ?? loginLoading;
  const error = registerError ?? errorLogIn;

  const handleSubmit = async e => {
    e.preventDefault();
    await registerCustomer({ email, password, acceptsMarketing });
  };

  const emailLabel = tGlobal('Email label', 'Email');
  const passwordLabel = tGlobal('Password label', 'Password');
  const errorText = tGlobal('Server error text', 'Something went wrong, please try again later.');
  const buttonLoadingText = tGlobal('Button loading text', 'Loading...');
  const registerButtonText = t('Register button text', 'Sign up');

  const createAccountUSPs = getResource(access)('Create Account USPs')?.richText;
  const subscribeToNewsletterCheckboxLabel = getResource(access)(
    'Registration Subscription Checkbox Label'
  )?.richText;
  const registrationDisclaimer = getResource(access)('Registration Disclaimer')?.richText;

  useEffect(() => {
    const loginWithMultiPass = async () => {
      // get email
      const oAuthEmail = session.user.email;
      // get multipass token
      const multipassToken = await getMultipassToken(oAuthEmail, currency);
      // call log in by passing multipassToken
      await loginCustomer({ multipassToken, isMultipass: true });
    };

    if (session?.user?.email) {
      loginWithMultiPass();
    }
  }, [session?.user?.email, currency]);

  return (
    <AccountWrapper
      loadingOverlay={!!(status === 'loading' || status === 'authenticated')}
      backgroundImage={getResourceImage(access)('Background image')}
    >
      <form className="w-full !py-6" onSubmit={e => handleSubmit(e)}>
        <h5 className="mb-4 font-nimbus-sans-extd-d text-[2.5rem] font-bold uppercase leading-[85%] tracking-[-0.02em] text-blue">
          {t('Register header text', 'Sign up')}
        </h5>

        <div className="mb-7 flex flex-col gap-6 md:mb-9 [&_br]:hidden">
          <p className="text-body-1 font-nimbus-sans-d text-blue">
            {t('Register Header Description')}
          </p>
          <RichText
            className="[&_p]:text-body-2"
            data={createAccountUSPs}
            options={richTextOptions(createAccountUSPs)}
          />
        </div>

        <div className="mb-6 flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <input
              name="email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              placeholder={`${emailLabel}*`}
              className="input text-body-1 w-full"
            />
            <input
              name="password"
              value={password}
              minLength={8}
              onChange={e => setPassword(e.target.value)}
              type="password"
              required
              placeholder={`${passwordLabel}*`}
              className="input text-body-1 w-full"
            />
          </div>
          <div className="flex flex-col gap-3">
            <label
              htmlFor="subscribeToNewsletter"
              className="flex items-start font-nimbus-sans-d text-[12px] leading-[1.4]"
            >
              <input
                className="mr-[10px] mt-0.5 !rounded-none checked:bg-blue"
                id="subscribeToNewsletter"
                type="checkbox"
                name="subscribeToNewsletter"
                required
                onChange={() => setAcceptsMarketing(state => !state)}
              />
              <span className="leading-none">
                <RichText
                  data={subscribeToNewsletterCheckboxLabel}
                  options={richTextOptions(subscribeToNewsletterCheckboxLabel)}
                />
              </span>
            </label>
            <div className="pl-6">
              <RichText
                data={registrationDisclaimer}
                options={richTextOptions(registrationDisclaimer)}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <button type="submit" className="button w-full">
              {loading ? buttonLoadingText : registerButtonText}
            </button>
            {error && (
              <p className="text-red-500 text-[12px]">{isString(error) ? error : errorText}</p>
            )}
          </div>
        </div>

        <div className="mb-7 flex flex-col gap-2 md:mb-9">
          <Button
            onClick={() => signIn('google')}
            className="[&>div]:flex [&>div]:gap-2.5"
            type="Button (Outline)"
          >
            {loading ? (
              <Spinner className="h-8" />
            ) : (
              <>
                <Icon className="h-4 w-4" type="google" />
                <span>{t('Continue With Google')}</span>
              </>
            )}
          </Button>
          {/* <Button
            onClick={() => signIn('facebook')}
            className="[&>div]:flex [&>div]:gap-2.5"
            type="Button (Outline)"
          >
            {loading ? (
              buttonLoadingText
            ) : (
              <>
                <Icon className="h-4 w-4" type="facebook" />
                <span> {t('Continue With Facebook')}</span>
              </>
            )}
          </Button> */}
        </div>

        <p className="text-body-2 flex gap-2 text-blue">
          {t('Already have an Account Text')}{' '}
          <a href={`${accountSlug}/login/`} className="underline">
            {t('Back to Log In Text')}
          </a>
        </p>
      </form>
    </AccountWrapper>
  );
};

export default Register;
